// @ts-nocheck
// components/LeadCaptureGate.tsx
// ✅ vNext — NO OTP: removed OTP modal + webhook. Submits immediately and routes to thank-you.

import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Platform,
  Animated,
} from 'react-native';
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { Sunrise, Sun, Moon, Clock, CheckCircle2 } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { NoCoursesEmailInput } from './NoCoursesEmailInput';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { getAccessibleFontFamily } from '@/utils/accessibilityHelpers';
import { Course } from '@/types/chat';
import { supabase } from '@/utils/supabase';

interface LeadCaptureGateProps {
  matchCount: number;
  userSummary: string;
  searchData: any;
  courses?: Course[];
  onSubmit: (data: {
    fullName: string;
    phone: string;
    selectedMentorCount: number;
    consentGiven: boolean;
    preferredTime?: 'morning' | 'afternoon' | 'evening';
  }) => Promise<void>;
  onSkip: () => void;
  onEmailSubmit?: (email: string) => Promise<void>;
  onComplete?: () => void;
}

// ✅ FIX #10 — improved phone normalization: validates length after stripping
function normalizeILPhone(raw: string): string {
  let p = (raw ?? '').toString().trim();
  p = p.replace(/[^\d]/g, '');
  if (p.startsWith('972') && p.length === 12) p = '0' + p.slice(3);
  if (p.startsWith('0') && p.length !== 10) return '';
  return p;
}

const KNOB_SIZE = 28;
const TRACK_HEIGHT = 6;
const TRACK_AREA_HEIGHT = 44;

function getCountTitle(n: number): string {
  if (n === 1) return 'הרלוונטי ביותר';
  if (n === 2) return 'השוואה בזמנים קצרים';
  return 'השוואה מקסימלית';
}

function getCountLabel(n: number): string {
  if (n === 1) return 'הרלוונטי ביותר';
  if (n === 2) return 'השוואה בזמנים קצרים';
  return 'השוואה מקסימלית';
}

function getCountDesc(selected: 1 | 2 | 3, maxAvailable: 1 | 2 | 3): string {
  if (maxAvailable === 1) {
    return 'יש כרגע התאמה אחת בלבד — נשלח את הפרטים לבעל הקורס המתאים ביותר כדי שיחזור אליך מהר.';
  }

  if (maxAvailable === 2) {
    if (selected === 1) return 'שולחים לבעל הקורס הכי מתאים. פחות שיחות, יותר דיוק.';
    return 'שולחים לשני בעלי קורסים רלוונטיים כדי להגדיל זמינות ומהירות בלי להציף.';
  }

  if (selected === 1) return 'שולחים רק לבעל הקורס המתאים ביותר. פחות רעש, יותר פוקוס.';
  if (selected === 2) return 'שולחים לשני בעלי קורסים כדי לקבל מענה מהיר ולהשוות בלי להציף.';
  return 'שולחים ל־3 בעלי קורסים כדי לקבל הכי הרבה אפשרויות ולהחליט בביטחון.';
}

export function LeadCaptureGate({
  matchCount,
  userSummary,
  searchData,
  courses = [],
  onSubmit,
  onSkip,
  onEmailSubmit,
  onComplete,
}: LeadCaptureGateProps) {
  const router = useRouter();
  const accessibility = useAccessibility();
  const a11ySettings = accessibility?.settings;
  const chatFontFamily = a11ySettings
    ? getAccessibleFontFamily(theme.fonts.regular, a11ySettings)
    : theme.fonts.regular;

  const scrollRef = useRef<ScrollView | null>(null);

  // ✅ if it comes from carousel / direct lead: go straight to details
  const skipCourseStep =
    !!searchData?.skip_course_step ||
    searchData?.source === 'voucher_carousel' ||
    searchData?.directLead === true;

  const [step, setStep] = useState<'courses' | 'details'>(() => (skipCourseStep ? 'details' : 'courses'));

  useEffect(() => {
    if (skipCourseStep) setStep('details');
  }, [skipCourseStep]);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(new Set());
  const [consentGiven, setConsentGiven] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ fullName?: string; phone?: string; consent?: string; selection?: string }>({});
  const [showTimeSelection, setShowTimeSelection] = useState(false);
  const [preferredTime, setPreferredTime] = useState<'morning' | 'afternoon' | 'evening' | null>(null);

  // courses shown: 1/2/3 according to matchCount (or list length)
  const topCourses = useMemo(() => {
    const n = Math.min(3, Math.max(0, matchCount || courses.length || 0));
    return (courses || []).slice(0, n);
  }, [courses, matchCount]);

  const maxOwnerOptions = useMemo(() => {
    const n = Math.min(3, Math.max(1, topCourses.length || 1));
    return (n === 1 ? 1 : n === 2 ? 2 : 3) as 1 | 2 | 3;
  }, [topCourses.length]);

  const optionValues = useMemo(() => {
    return Array.from({ length: maxOwnerOptions }, (_, i) => (i + 1) as 1 | 2 | 3);
  }, [maxOwnerOptions]);

  const [selectedOwnerCount, setSelectedOwnerCount] = useState<1 | 2 | 3>(1);
  const [trackWidth, setTrackWidth] = useState(0);
  const knobX = useRef(new Animated.Value(0)).current;

  const cleanPhone = useMemo(() => normalizeILPhone(phone), [phone]);

  // title topic (no "AI")
  const topicText = useMemo(() => {
    const t = (searchData?.topic || '').toString().trim();
    if (t) return t;
    const s = (userSummary || '').toString().trim();
    if (s) return s;
    return 'התחום שביקשת';
  }, [searchData, userSummary]);

  const heroTopicText = useMemo(() => {
    const base = (topicText || '').toString().trim();
    if (!base) return 'התחום שביקשת';

    return base
      .replace(/\s*בעזרת\s*ai\b/gi, '')
      .replace(/\s*בינה\s*מלאכותית\b/g, '')
      .trim();
  }, [topicText]);

  const selectedPrimaryCourseName = useMemo(() => {
    const selectedIds = Array.from(selectedCourseIds);
    const primaryId = selectedIds[0];
    const found = (primaryId ? topCourses.find((c: any) => c.id === primaryId) : null) || topCourses[0];
    return (found as any)?.marketing_title || (found as any)?.name || 'הקורס';
  }, [selectedCourseIds, topCourses]);

  const trackInnerWidth = useMemo(() => Math.max(trackWidth - KNOB_SIZE, 0), [trackWidth]);

  const optionPositions = useMemo(() => {
    if (trackInnerWidth <= 0) return optionValues.map(() => 0);
    if (maxOwnerOptions === 1) return [trackInnerWidth];
    if (maxOwnerOptions === 2) return [trackInnerWidth, 0];
    return [trackInnerWidth, trackInnerWidth / 2, 0];
  }, [trackInnerWidth, maxOwnerOptions, optionValues]);

  const selectedCountDesc = useMemo(
    () => getCountDesc(selectedOwnerCount, maxOwnerOptions),
    [selectedOwnerCount, maxOwnerOptions]
  );

  const animateKnobToCount = useCallback(
    (count: 1 | 2 | 3) => {
      const clamped = Math.min(count, maxOwnerOptions) as 1 | 2 | 3;
      const idx = Math.max(0, Math.min(optionPositions.length - 1, clamped - 1));
      const target = optionPositions[idx] ?? 0;

      Animated.spring(knobX, {
        toValue: target,
        useNativeDriver: false,
        tension: 120,
        friction: 16,
      }).start();
    },
    [knobX, optionPositions, maxOwnerOptions]
  );

  useEffect(() => {
    setSelectedOwnerCount((prev) => Math.min(prev, maxOwnerOptions) as 1 | 2 | 3);
  }, [maxOwnerOptions]);

  useEffect(() => {
    if (trackWidth > 0) {
      const idx = Math.max(0, Math.min(optionPositions.length - 1, selectedOwnerCount - 1));
      knobX.setValue(optionPositions[idx] ?? 0);
    }
  }, [trackWidth, optionPositions, selectedOwnerCount, knobX]);

  useEffect(() => {
    if (trackWidth > 0) animateKnobToCount(selectedOwnerCount);
  }, [selectedOwnerCount, trackWidth, animateKnobToCount]);

  useEffect(() => {
    const count = Math.min(Math.max(selectedCourseIds.size, 1), maxOwnerOptions) as 1 | 2 | 3;
    if (count !== selectedOwnerCount) setSelectedOwnerCount(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseIds.size, maxOwnerOptions]);

  useEffect(() => {
    if (!topCourses.length) return;

    setSelectedCourseIds((prev) => {
      const availableIds = topCourses.map((c: any) => c.id);
      const currentOrdered = Array.from(prev).filter((id) => availableIds.includes(id));
      if (currentOrdered.length > 0) return prev;

      const firstId = (topCourses[0] as any)?.id;
      if (!firstId) return prev;
      return new Set([firstId]);
    });

    if (errors.selection) setErrors((prev) => ({ ...prev, selection: undefined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topCourses]);

  useEffect(() => {
    if (skipCourseStep) setSelectedOwnerCount(1);
  }, [skipCourseStep]);

  const toggleCourseSelection = (courseId: string) => {
    setSelectedCourseIds((prev) => {
      const next = new Set(prev);

      if (next.has(courseId)) {
        if (next.size <= 1) return next;
        next.delete(courseId);
      } else {
        const limit = maxOwnerOptions;
        if (next.size >= limit) {
          const oldest = next.values().next().value;
          if (oldest) next.delete(oldest);
        }
        next.add(courseId);
      }

      if (errors.selection) setErrors({ ...errors, selection: undefined });
      return next;
    });
  };

  const handleSelectOwnerCount = (n: 1 | 2 | 3) => {
    const clamped = Math.min(n, maxOwnerOptions) as 1 | 2 | 3;
    setSelectedOwnerCount(clamped);
    animateKnobToCount(clamped);

    setSelectedCourseIds((prev) => {
      const availableIds = topCourses.map((c: any) => c.id);
      if (availableIds.length === 0) return prev;

      const currentOrdered = availableIds.filter((id) => prev.has(id));
      const chosen: string[] = [];

      for (const id of currentOrdered) {
        if (chosen.length >= clamped) break;
        chosen.push(id);
      }
      for (const id of availableIds) {
        if (chosen.length >= clamped) break;
        if (!chosen.includes(id)) chosen.push(id);
      }

      return new Set(chosen.slice(0, clamped));
    });

    if (errors.selection) setErrors((prev) => ({ ...prev, selection: undefined }));
  };

  const canProceedToDetails = useMemo(
    () => topCourses.length > 0 && selectedCourseIds.size >= 1,
    [topCourses.length, selectedCourseIds.size]
  );

  const goToDetails = () => {
    if (!canProceedToDetails) {
      setErrors((prev) => ({ ...prev, selection: 'יש לבחור לפחות קורס אחד' }));
      return;
    }
    setStep('details');
    setTimeout(() => {
      // @ts-ignore
      scrollRef.current?.scrollTo?.({ y: 0, animated: true });
    }, 30);
  };

  const backToCourses = () => {
    setStep('courses');
    setTimeout(() => {
      // @ts-ignore
      scrollRef.current?.scrollTo?.({ y: 0, animated: true });
    }, 30);
  };

  // If no courses found, show email capture
  if (matchCount === 0 && onEmailSubmit) {
    return (
      <View style={styles.container}>
        <ScrollView
          ref={(r) => (scrollRef.current = r)}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.card}>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onSkip}
              accessibilityLabel="סגור חלון"
              accessibilityRole="button"
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>

            <Text style={styles.title}>לא מצאנו התאמות מדויקות</Text>

            <NoCoursesEmailInput onEmailSubmit={onEmailSubmit} />

            <TouchableOpacity style={styles.skipButton} onPress={onSkip} accessibilityLabel="סגור" accessibilityRole="button">
              <Text style={styles.skipButtonText}>סגור</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    );
  }

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!fullName.trim()) newErrors.fullName = 'נא להזין שם מלא';
    if (!cleanPhone || !/^05\d{8}$/.test(cleanPhone)) newErrors.phone = 'מספר טלפון לא תקין (דוגמה: 0501234567)';
    if (!consentGiven) newErrors.consent = 'יש לאשר העברת פרטים';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildChosenIds = (selectedCount: 1 | 2 | 3) => {
    const allowedIds = topCourses.map((c: any) => c.id);
    const selectedOrdered = topCourses.map((c: any) => c.id).filter((id: string) => selectedCourseIds.has(id));
    const chosen = selectedOrdered.slice(0, selectedCount);

    if (chosen.length < selectedCount) {
      for (const id of allowedIds) {
        if (chosen.length >= selectedCount) break;
        if (!chosen.includes(id)) chosen.push(id);
      }
    }

    if (chosen.length === 0 && (topCourses[0] as any)?.id) chosen.push((topCourses[0] as any).id);
    return chosen.slice(0, selectedCount);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const selectedCount = (skipCourseStep ? 1 : Math.min(selectedOwnerCount, maxOwnerOptions)) as 1 | 2 | 3;
      const chosenIds = buildChosenIds(selectedCount);
      const firstCourseId = chosenIds[0] || null;

      const payloadForTempLead = {
        selected_course_ids: chosenIds,
        selected_count: chosenIds.length,
        matchCount: topCourses.length,
        topic: heroTopicText,
        userSummary,
        searchData,
        consentGiven,
        preferredTime: preferredTime || '',
        selected_owner_count: selectedCount,
        marketing_titles: Object.fromEntries(
          topCourses
            .filter((c: any) => chosenIds.includes(c.id))
            .map((c: any) => [c.id, c.marketing_title || c.name || ''])
        ),
      };

      // Keep temp_leads insert (optional analytics / ops)
      const { error: tempLeadError } = await supabase.from('temp_leads').insert({
        full_name: fullName.trim(),
        phone: cleanPhone,
        course_id: firstCourseId,
        source: skipCourseStep ? 'voucher_carousel_lead_gate' : 'course_match_lead_gate',
        status: 'pending',
        message: JSON.stringify(payloadForTempLead),
        preferred_time: preferredTime || '',
        learning_type: 'phone',
        notes: '',
      });

      if (tempLeadError) {
        console.error('[LeadCaptureGate] temp_leads insert error:', tempLeadError);
        // We still continue UX if backend lead submission works
      }

      // ✅ NO OTP: submit immediately
      await onSubmit({
        fullName: fullName.trim(),
        phone: cleanPhone,
        selectedMentorCount: selectedCount, // ✅ FIX: must reflect user's choice (1/2/3)
        consentGiven,
        preferredTime: preferredTime || undefined,
      });

      const chosenCourses = topCourses
        .filter((c: any) => chosenIds.includes(c.id))
        .map((c: any) => ({
          id: c.id,
          name: c.name,
          marketing_title: c.marketing_title || '',
          mentor: c.mentor || '',
        }));

      if (onComplete) onComplete();

      const availability = (preferredTime || 'morning') as any;
      const courseName = selectedPrimaryCourseName;

      router.replace({
        pathname: '/thank-you',
        params: {
          courseName,
          availability,
          selectedCourseIds: JSON.stringify(chosenIds),
          selectedCourses: JSON.stringify(chosenCourses),
          userSummary: userSummary || '',
          searchData: JSON.stringify(searchData || {}),
        },
      });
    } catch (error) {
      console.error('[LeadCaptureGate] Submit error:', error);
      setErrors({ consent: 'אירעה שגיאה, נסה שוב' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCountButton = (count: 1 | 2 | 3) => {
    const isSelected = selectedOwnerCount === count;
    const isDisabled = count > maxOwnerOptions;

    return (
      <TouchableOpacity
        key={`count-btn-${count}`}
        onPress={() => handleSelectOwnerCount(count)}
        disabled={isDisabled}
        accessibilityRole="button"
        accessibilityLabel={`בחר ${count}`}
        style={[
          styles.countButton,
          isSelected && styles.countButtonActive,
          isDisabled && styles.countButtonDisabled,
        ]}
      >
        <Text style={[styles.countButtonNumber, isSelected && styles.countButtonNumberActive]}>{count}</Text>
        <Text style={[styles.countButtonLabel, isSelected && styles.countButtonLabelActive]}>{getCountLabel(count)}</Text>
      </TouchableOpacity>
    );
  };

  const selectedCount = Math.min(selectedOwnerCount, maxOwnerOptions) as 1 | 2 | 3;

  if (step === 'details') {
    return (
      <View style={styles.container}>
        <ScrollView
          ref={(r) => (scrollRef.current = r)}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.card}>
            <TouchableOpacity style={styles.closeButton} onPress={onSkip} accessibilityLabel="סגור חלון" accessibilityRole="button">
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>

            <Text style={styles.heroTitle}>השארת פרטים</Text>
            <Text style={styles.heroSubtitle}>
              {skipCourseStep ? 'נחבר אותך לבעל הקורס תוך זמן קצר' : 'נחבר אותך לקורסים שבחרת תוך זמן קצר'}
            </Text>

            {!skipCourseStep && (
              <TouchableOpacity
                style={styles.secondaryActionButton}
                onPress={backToCourses}
                accessibilityLabel="חזרה לבחירת קורסים"
                accessibilityRole="button"
              >
                <Text style={styles.secondaryActionButtonText}>חזרה לבחירת קורסים</Text>
              </TouchableOpacity>
            )}

            <View style={styles.inputContainer}>
              <Text style={styles.label}>שם מלא</Text>
              <TextInput
                style={[styles.input, errors.fullName && styles.inputError]}
                value={fullName}
                onChangeText={(text) => {
                  setFullName(text);
                  if (errors.fullName) setErrors({ ...errors, fullName: undefined });
                }}
                placeholder="הזן שם מלא"
                placeholderTextColor="rgba(255, 255, 255, 0.4)"
                accessibilityLabel="שדה שם מלא"
              />
              {errors.fullName && <Text style={styles.errorText}>{errors.fullName}</Text>}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>טלפון</Text>
              <TextInput
                style={[styles.input, errors.phone && styles.inputError]}
                value={phone}
                onChangeText={(text) => {
                  setPhone(text);
                  if (errors.phone) setErrors({ ...errors, phone: undefined });
                }}
                placeholder="050-1234567"
                placeholderTextColor="rgba(255, 255, 255, 0.4)"
                keyboardType="phone-pad"
                accessibilityLabel="שדה מספר טלפון"
              />
              {errors.phone && <Text style={styles.errorText}>{errors.phone}</Text>}
            </View>

            <TouchableOpacity
              style={styles.checkboxContainer}
              onPress={() => {
                setConsentGiven(!consentGiven);
                if (errors.consent) setErrors({ ...errors, consent: undefined });
              }}
              accessibilityLabel="אישור העברת פרטים"
              accessibilityRole="checkbox"
              accessibilityState={{ checked: consentGiven }}
            >
              <View style={[styles.checkbox, consentGiven && styles.checkboxChecked]}>
                {consentGiven && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={[styles.checkboxLabel, { fontFamily: chatFontFamily }]}>
                אני מאשר/ת להעביר את הפרטים שלי לצורך יצירת קשר
              </Text>
            </TouchableOpacity>
            {errors.consent && <Text style={styles.errorText}>{errors.consent}</Text>}

            <TouchableOpacity
              style={styles.customizeButton}
              onPress={() => setShowTimeSelection(!showTimeSelection)}
              accessibilityLabel={showTimeSelection ? 'הסתר בחירת זמן' : 'בחירת זמן מועדף ליצירת קשר'}
              accessibilityRole="button"
            >
              <Clock size={20} color={theme.colors.primary} />
              <Text style={styles.customizeButtonText}>
                {showTimeSelection ? 'הסתר בחירת זמן' : 'בחירת זמן מועדף ליצירת קשר'}
              </Text>
            </TouchableOpacity>

            {showTimeSelection && (
              <View style={styles.timeSelectionContainer}>
                <Text style={styles.label}>מתי נוח לך לשוחח?</Text>
                <View style={styles.timeOptions}>
                  <TouchableOpacity
                    style={[styles.timeOption, preferredTime === 'morning' && styles.timeOptionActive]}
                    onPress={() => setPreferredTime('morning')}
                    accessibilityLabel="בוקר 08:00-12:00"
                    accessibilityRole="button"
                  >
                    <Sunrise size={24} color={preferredTime === 'morning' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)'} />
                    <Text style={[styles.timeOptionText, preferredTime === 'morning' && styles.timeOptionTextActive]}>בוקר</Text>
                    <Text style={styles.timeOptionSubtext}>08:00-12:00</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.timeOption, preferredTime === 'afternoon' && styles.timeOptionActive]}
                    onPress={() => setPreferredTime('afternoon')}
                    accessibilityLabel="צהריים 12:00-16:00"
                    accessibilityRole="button"
                  >
                    <Sun size={24} color={preferredTime === 'afternoon' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)'} />
                    <Text style={[styles.timeOptionText, preferredTime === 'afternoon' && styles.timeOptionTextActive]}>צהריים</Text>
                    <Text style={styles.timeOptionSubtext}>12:00-16:00</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.timeOption, preferredTime === 'evening' && styles.timeOptionActive]}
                    onPress={() => setPreferredTime('evening')}
                    accessibilityLabel="ערב 16:00-20:00"
                    accessibilityRole="button"
                  >
                    <Moon size={24} color={preferredTime === 'evening' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)'} />
                    <Text style={[styles.timeOptionText, preferredTime === 'evening' && styles.timeOptionTextActive]}>ערב</Text>
                    <Text style={styles.timeOptionSubtext}>16:00-20:00</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            <TouchableOpacity
              style={[
                styles.submitButton,
                (!consentGiven || isSubmitting || !fullName.trim() || !phone.trim()) && styles.submitButtonDisabled,
              ]}
              onPress={handleSubmit}
              disabled={!consentGiven || isSubmitting || !fullName.trim() || !phone.trim()}
              accessibilityLabel="לסיום לחצו כאן"
              accessibilityRole="button"
            >
              {isSubmitting ? <ActivityIndicator color="#fff" /> : <Text style={styles.submitButtonText}>לסיום לחצו כאן</Text>}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        ref={(r) => (scrollRef.current = r)}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.card}>
          <TouchableOpacity style={styles.closeButton} onPress={onSkip} accessibilityLabel="סגור חלון" accessibilityRole="button">
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>

          <Text style={styles.heroTitle}>
            על סמך התשובות שהעברתם, אותרו {topCourses.length} {topCourses.length === 1 ? 'קורס' : 'קורסים'} ל{heroTopicText}
          </Text>

          <Text style={styles.heroSubtitle}>קודם תראו את שמות הקורסים — ורק אחר כך נפתח השארת פרטים</Text>

          {topCourses.length > 0 && (
            <View style={styles.coursesListContainer}>
              {topCourses.map((course: any, index: number) => (
                <TouchableOpacity
                  key={course.id}
                  style={[styles.courseOption, selectedCourseIds.has(course.id) && styles.courseOptionSelected]}
                  onPress={() => toggleCourseSelection(course.id)}
                  accessibilityLabel={`קורס ${index + 1}: ${course.marketing_title || course.name}`}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: selectedCourseIds.has(course.id) }}
                >
                  <View style={styles.courseOptionContent}>
                    <View style={[styles.courseCheckbox, selectedCourseIds.has(course.id) && styles.courseCheckboxChecked]}>
                      {selectedCourseIds.has(course.id) && <CheckCircle2 size={20} color="#FFFFFF" strokeWidth={2.5} />}
                    </View>
                    <Text style={[styles.courseOptionText, selectedCourseIds.has(course.id) && styles.courseOptionTextSelected]}>
                      {course.marketing_title || course.name}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}

              {errors.selection && <Text style={styles.errorText}>{errors.selection}</Text>}
            </View>
          )}

          <View style={styles.whyBox}>
            <Text style={styles.whyText}>
              הקורסים מוצגים בתצורה הזו משום שאנחנו לא רוצים שתושפעו משמות גדולים והרעש שהם עושים ברשת - בחרו חכם.
            </Text>
          </View>

          <View style={styles.progressContainer}>
            <View style={styles.progressHeader}>
              <Text style={styles.progressTitle}>איך לבחור עם כמה בעלי קורסים להמשיך?</Text>
              <Text style={styles.progressHint}>{maxOwnerOptions === 1 ? 'יש התאמה אחת' : `בחר 1–${maxOwnerOptions}`}</Text>
            </View>

            <View style={styles.countButtonsRow}>{([1, 2, 3] as const).map(renderCountButton)}</View>

            <View style={styles.progressTrackArea} onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}>
              <View style={styles.progressTrack} />
              <Animated.View
                style={[
                  styles.progressKnob,
                  {
                    left: knobX,
                    opacity: maxOwnerOptions === 1 ? 0.85 : 1,
                  },
                ]}
              >
                <View style={styles.progressKnobInner} />
              </Animated.View>
            </View>

            <Text style={styles.progressDesc}>
              <Text style={{ fontFamily: theme.fonts.semiBold }}>{getCountTitle(selectedCount)}: </Text>
              {selectedCountDesc}
            </Text>
          </View>

          <TouchableOpacity
            style={[styles.primaryActionButton, !canProceedToDetails && styles.primaryActionButtonDisabled]}
            onPress={goToDetails}
            disabled={!canProceedToDetails}
            accessibilityLabel="המשך"
            accessibilityRole="button"
          >
            <Text style={styles.primaryActionButtonText}>המשך</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  scrollView: {
    maxHeight: Platform.OS === 'web' ? 600 : undefined,
  },
  scrollContent: {
    padding: 16,
  },
  card: {
    backgroundColor: 'rgba(30, 30, 40, 0.95)',
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.3)',
    ...Platform.select({
      web: {
        boxShadow: '0 8px 32px rgba(147, 51, 234, 0.2)',
      },
      default: {
        shadowColor: '#9333ea',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        elevation: 8,
      },
    }),
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    left: 12,
    width: 34,
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.35)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  closeButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '800',
    fontFamily: theme.fonts.bold,
    lineHeight: 18,
  },

  title: {
    marginTop: 8,
    marginBottom: 12,
    fontSize: 18,
    color: '#fff',
    textAlign: 'center',
    fontFamily: theme.fonts.bold,
    lineHeight: 24,
  },
  skipButton: {
    marginTop: 14,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.4)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  skipButtonText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: theme.fonts.semiBold,
  },

  heroTitle: {
    marginTop: 6,
    marginBottom: 10,
    fontSize: 18,
    color: '#fff',
    textAlign: 'center',
    fontFamily: theme.fonts.bold,
    lineHeight: 24,
  },
  heroSubtitle: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.75)',
    textAlign: 'center',
    fontFamily: theme.fonts.regular,
    marginBottom: 14,
    lineHeight: 20,
  },

  coursesListContainer: {
    marginBottom: 12,
  },
  courseOption: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
    borderWidth: 2,
    borderColor: 'rgba(147, 51, 234, 0.3)',
  },
  courseOptionSelected: {
    borderColor: theme.colors.primary,
    backgroundColor: 'rgba(147, 51, 234, 0.15)',
  },
  courseOptionContent: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
  },
  courseCheckbox: {
    width: 28,
    height: 28,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: 'rgba(147, 51, 234, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  courseCheckboxChecked: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  courseOptionText: {
    flex: 1,
    fontSize: 15,
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: 20,
    fontFamily: theme.fonts.regular,
    textAlign: 'right',
  },
  courseOptionTextSelected: {
    color: '#FFFFFF',
    fontFamily: theme.fonts.semiBold,
  },

  whyBox: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.22)',
    backgroundColor: 'rgba(255,255,255,0.03)',
    padding: 12,
    marginBottom: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  whyText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.82)',
    fontFamily: theme.fonts.regular,
    textAlign: 'center',
    lineHeight: 18,
  },

  progressContainer: {
    marginBottom: 16,
    paddingTop: 10,
    paddingBottom: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.25)',
    paddingHorizontal: 12,
  },
  progressHeader: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  progressTitle: {
    color: '#fff',
    fontSize: 13,
    fontFamily: theme.fonts.semiBold,
    flex: 1,
    textAlign: 'right',
    marginLeft: 10,
  },
  progressHint: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    textAlign: 'right',
  },

  countButtonsRow: {
    flexDirection: 'row-reverse',
    gap: 10,
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  countButton: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.35)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    paddingVertical: 10,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countButtonActive: {
    borderColor: 'rgba(255,255,255,0.85)',
    backgroundColor: 'rgba(147, 51, 234, 0.22)',
  },
  countButtonDisabled: {
    opacity: 0.35,
  },
  countButtonNumber: {
    color: '#fff',
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    marginBottom: 4,
  },
  countButtonNumberActive: {
    color: '#fff',
  },
  countButtonLabel: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 11,
    fontFamily: theme.fonts.medium,
    textAlign: 'center',
    lineHeight: 14,
  },
  countButtonLabelActive: {
    color: '#fff',
  },

  progressTrackArea: {
    height: TRACK_AREA_HEIGHT,
    width: '100%',
    position: 'relative',
    justifyContent: 'center',
  },
  progressTrack: {
    height: TRACK_HEIGHT,
    borderRadius: 999,
    backgroundColor: 'rgba(147, 51, 234, 0.25)',
    width: '100%',
  },
  progressKnob: {
    position: 'absolute',
    top: (TRACK_AREA_HEIGHT - KNOB_SIZE) / 2,
    width: KNOB_SIZE,
    height: KNOB_SIZE,
    borderRadius: KNOB_SIZE / 2,
    backgroundColor: 'rgba(147, 51, 234, 0.85)',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressKnobInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#FFFFFF',
    opacity: 0.9,
  },
  progressDesc: {
    marginTop: 8,
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    fontFamily: theme.fonts.regular,
    textAlign: 'right',
    lineHeight: 18,
  },

  inputContainer: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 8,
    fontFamily: theme.fonts.medium,
    textAlign: 'right',
  },
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.3)',
    fontFamily: theme.fonts.regular,
    textAlign: 'right',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  errorText: {
    color: '#ef4444',
    fontSize: 12,
    marginTop: 4,
    fontFamily: theme.fonts.regular,
    textAlign: 'right',
  },

  checkboxContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    marginBottom: 14,
    gap: 12,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: 'rgba(147, 51, 234, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  checkmark: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    fontFamily: theme.fonts.bold,
  },
  checkboxLabel: {
    flex: 1,
    fontSize: 14,
    color: '#fff',
    lineHeight: 20,
    fontFamily: theme.fonts.regular,
    textAlign: 'right',
  },

  customizeButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.5)',
    backgroundColor: 'rgba(147, 51, 234, 0.1)',
    marginBottom: 16,
  },
  customizeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.primary,
    fontFamily: theme.fonts.medium,
  },
  timeSelectionContainer: {
    marginBottom: 20,
  },
  timeOptions: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'space-between',
  },
  timeOption: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 8,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: 'rgba(147, 51, 234, 0.3)',
    backgroundColor: 'transparent',
    alignItems: 'center',
    gap: 4,
  },
  timeOptionActive: {
    borderColor: theme.colors.primary,
    backgroundColor: 'rgba(147, 51, 234, 0.2)',
  },
  timeOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.6)',
    fontFamily: theme.fonts.medium,
  },
  timeOptionTextActive: {
    color: '#FFFFFF',
  },
  timeOptionSubtext: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.4)',
    fontFamily: theme.fonts.regular,
  },

  submitButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    marginBottom: 12,
  },
  submitButtonDisabled: {
    backgroundColor: 'rgba(147, 51, 234, 0.3)',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#fff',
    fontFamily: theme.fonts.bold,
  },

  primaryActionButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    marginBottom: 14,
  },
  primaryActionButtonDisabled: {
    backgroundColor: 'rgba(147, 51, 234, 0.3)',
  },
  primaryActionButtonText: {
    fontSize: 16,
    fontWeight: '800',
    color: '#fff',
    fontFamily: theme.fonts.bold,
  },

  secondaryActionButton: {
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(147, 51, 234, 0.45)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    marginBottom: 12,
  },
  secondaryActionButtonText: {
    fontSize: 13,
    color: '#fff',
    fontFamily: theme.fonts.semiBold,
  },
});